import React from 'react';
import {
  Button,
  ButtonGroup,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  Box,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { ErrorOutline } from '@mui/icons-material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import SpreadsheetViewer, { SpreadsheetViewerRef } from './SpreadsheetViewer';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { VIEW_MODES, TABS_SX } from '../utils';
import type { ViewMode } from '../utils';
import type { SpreadsheetViewerProps } from '../types/spreadsheetTypes';
import SideBySideView from './SideBySideView';
import TopBottomView from './TopBottomView';
import { tabLabel } from './ViewerUtils';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from 'src/components/ui/resizable';

interface SpreadsheetViewersProps {
  originalData: unknown[][];
  transformedData: unknown[][];
  isLoadingOriginal: boolean; // Includes parsing operations
  isLoadingTransformed: boolean; // Includes transforming and parsing operations
  transformError: string | null;
  transformationError?: string | null; // Error during transformation process
  // Header props
  title?: string;
  originalTitle?: string;
  transformedTitle?: string;
  headerChildren?: React.ReactNode;
  // Controls props
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workbook: any;
  selectedSheet?: string;
  onSheetChange?: (sheetName: string, clearConversation: () => void) => void;
  onConversationClear?: () => void;

  // Callback when original data is edited
  onOriginalDataChange?: (data: unknown[][]) => void;
  // Callback when transformed data is edited
  onTransformedDataChange?: (data: unknown[][]) => void;
  filename?: string;
  // Optional view mode override (defaults to sideBySide)
  initialViewMode?: ViewMode;
  // Edit permissions
  canEditOriginal?: boolean;
  canEditTransformed?: boolean;
}

// Main SpreadsheetViewers Component
const SpreadsheetViewers: React.FC<SpreadsheetViewersProps> = ({
  originalData,
  transformedData,
  isLoadingOriginal,
  isLoadingTransformed,
  transformError,
  transformationError,
  title,
  originalTitle,
  transformedTitle,
  headerChildren,
  workbook,
  selectedSheet,
  onSheetChange,
  onConversationClear,
  onOriginalDataChange,
  onTransformedDataChange,
  filename,
  initialViewMode = VIEW_MODES.sideBySide,
  canEditOriginal = false,
  canEditTransformed = true,
}) => {
  // Internal view mode state management
  const [viewMode, setViewMode] = React.useState<ViewMode>(initialViewMode);
  // Refs for accessing spreadsheet viewers
  const originalViewerRef = React.useRef<SpreadsheetViewerRef>(null!);
  const transformedViewerRef = React.useRef<SpreadsheetViewerRef>(null!);

  // Single viewer component for when there's no transformed data
  const SingleViewer: React.FC<{
    viewer: React.ReactNode;
    isLoading: boolean;
  }> = ({ viewer, isLoading }) => {
    return (
      <Paper
        elevation={2}
        sx={{
          height: '800px',
          border: '1px solid',
          borderColor: 'info.main',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={false}
          sx={{
            ...TABS_SX,
            flexShrink: 0,
          }}
        >
          <Tab label={tabLabel('Data', isLoading)} />
        </Tabs>
        <MDBox flex="1 1 auto" overflow="auto">
          {viewer}
        </MDBox>
      </Paper>
    );
  };

  const createSpreadsheetViewer = (
    props: SpreadsheetViewerProps & {
      ref?: React.RefObject<SpreadsheetViewerRef>;
      onDataChange?: (data: unknown[][]) => void;
      dataType?: 'original' | 'transformed';
      readOnly?: boolean;
    },
  ) => (
    <SpreadsheetViewer
      ref={props.ref}
      key={props.key}
      id={props.id}
      data={props.data}
      loading={props.loading}
      error={props.error}
      height="100%"
      showSelectionSummary={true}
      onDataChange={props.onDataChange}
      filename={filename}
      readOnly={props.readOnly ?? true}
    />
  );

  const originalDataViewer = createSpreadsheetViewer({
    ref: originalViewerRef,
    key: 'original',
    data: originalData,
    loading: isLoadingOriginal,
    error: null,
    onDataChange: onOriginalDataChange,
    dataType: 'original',
    readOnly: !canEditOriginal,
  });

  // Create a placeholder spinner for when transformed data is loading but not yet available
  const loader = (
    <LoadingSpinner text="Processing data..." minHeight="100%" size={48} />
  );

  // Create error display for transformation errors
  const transformationErrorDisplay = () => {
    if (!transformationError) return null;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        p={3}
        bgcolor="error.light"
        borderRadius={1}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <ErrorOutline color="error" sx={{ mr: 1, fontSize: 32 }} />
          <MDTypography variant="h6" color="error.main">
            Transformation Error
          </MDTypography>
        </Box>
        <MDTypography
          variant="body2"
          color="error.dark"
          textAlign="center"
          maxWidth="400px"
        >
          {transformationError}
        </MDTypography>
      </Box>
    );
  };

  const noData = (
    <Box display="flex" alignItems="center" justifyContent="center">
      <MDTypography variant="body1" color="success.main">
        All data was filtered out.
      </MDTypography>
    </Box>
  );

  // Determine what to show in the transformed panel with correct priority order:
  // 1. Error display if there's a transformation error
  // 2. Loading spinner if currently loading/transforming
  // 3. Data viewer if we have data (length > 1)
  // 4. "All data was filtered out" message if no loading and no data
  const getTransformedContent = () => {
    if (transformationError) {
      return transformationErrorDisplay();
    }

    if (isLoadingTransformed) {
      console.log('🔄 SpreadsheetViewers: Still loading transformed data');
      return loader;
    }

    if (transformedData.length > 1) {
      return createSpreadsheetViewer({
        ref: transformedViewerRef,
        key: 'transformed',
        id: 'transformed-spreadsheet',
        data: transformedData,
        loading: isLoadingTransformed,
        error: transformError,
        onDataChange: onTransformedDataChange,
        dataType: 'transformed',
        readOnly: !canEditTransformed,
      });
    }

    return noData;
  };

  const transformedDataViewer = getTransformedContent();

  // Side-by-side view with AI chat layout
  const SideBySideViewWithAIChat = () => {
    // Always use side-by-side view, with spinner when data is loading
    return (
      <SideBySideView
        originalDataViewer={originalDataViewer}
        transformedDataViewer={transformedDataViewer}
        isLoadingOriginal={isLoadingOriginal}
        isLoadingTransformed={isLoadingTransformed}
        originalTitle={originalTitle}
        transformedTitle={transformedTitle}
      />
    );
  };

  // Render controls
  const renderControls = () => (
    <MDBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
      minHeight="48px"
    >
      {/* Left section - Title or Sheet selection */}
      <MDBox flex={1} display="flex" alignItems="center" gap={2}>
        {title && (
          <MDTypography variant="h6" component="h2">
            {title}
          </MDTypography>
        )}
        {selectedSheet && workbook && (
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {workbook.SheetNames.map((name: string) => (
              <Button
                key={name}
                variant={selectedSheet === name ? 'contained' : 'outlined'}
                color="info"
                size="small"
                onClick={() =>
                  onSheetChange?.(name, () => onConversationClear?.())
                }
                sx={{
                  minWidth: '120px',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: selectedSheet === name ? 1900 : 400,
                  backgroundColor: 'transparent',
                  color: selectedSheet === name ? 'white' : 'text.primary',
                  borderWidth: selectedSheet === name ? '5px' : '1px',
                  borderColor: selectedSheet === name ? 'black' : 'grey.300',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    borderColor: selectedSheet === name ? 'black' : 'grey.400',
                  },
                }}
              >
                {name}
              </Button>
            ))}
          </MDBox>
        )}
      </MDBox>

      {/* Right section - Header children and view mode controls */}
      <MDBox display="flex" alignItems="center" gap={2}>
        {headerChildren}
        {/* View mode controls - hide for single-view mode */}
        {viewMode !== VIEW_MODES.singleView && (
          <ButtonGroup variant="outlined" size="small">
            <Tooltip title="Side by Side">
              <IconButton
                color={viewMode === VIEW_MODES.sideBySide ? 'info' : 'default'}
                onClick={() => setViewMode(VIEW_MODES.sideBySide)}
                id="spreadsheet-viewers-side-by-side-button"
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Top and Bottom">
              <IconButton
                color={viewMode === VIEW_MODES.topBottom ? 'info' : 'default'}
                onClick={() => setViewMode(VIEW_MODES.topBottom)}
                id="spreadsheet-viewers-top-bottom-button"
              >
                <ViewStreamIcon />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        )}
      </MDBox>
    </MDBox>
  );

  // Render the appropriate view component based on viewMode
  const renderView = () => {
    switch (viewMode) {
      case VIEW_MODES.sideBySide:
        return <SideBySideViewWithAIChat />;
      case VIEW_MODES.topBottom:
        return (
          <TopBottomView
            originalDataViewer={originalDataViewer}
            transformedDataViewer={transformedDataViewer}
            isLoadingOriginal={isLoadingOriginal}
            isLoadingTransformed={isLoadingTransformed}
            originalTitle={originalTitle}
            transformedTitle={transformedTitle}
          />
        );
      case VIEW_MODES.singleView:
        return (
          <SingleViewer
            viewer={originalDataViewer}
            isLoading={isLoadingOriginal}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <MDBox display="flex" flexDirection="column">
          {/* Hide controls entirely for single-view mode */}
          {viewMode !== VIEW_MODES.singleView && renderControls()}

          {/* Render view with proper resizing structure based on mode */}
          {viewMode === VIEW_MODES.singleView ? (
            <MDBox style={{ height: '800px' }}>{renderView()}</MDBox>
          ) : (
            <ResizablePanelGroup
              direction="vertical"
              style={{ height: '800px' }}
            >
              {/* Main content area that can be resized vertically */}
              <ResizablePanel defaultSize={85} minSize={30}>
                {renderView()}
              </ResizablePanel>

              <ResizableHandle />
            </ResizablePanelGroup>
          )}
        </MDBox>
      </CardContent>
    </Card>
  );
};

export default React.memo(SpreadsheetViewers);
